import { useAuth } from "@clerk/clerk-react";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  Drawer,
  FormHelperText,
  Grid,
  IconButton,
  Link,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { AlertContext } from "contexts/Alert";
import { CheckoutContext } from "contexts/Checkout";
import { OrganizationUserContext } from "contexts/Organization";
import { SubscriptionContext } from "contexts/Subscription";
import { useContext, useEffect, useState } from "react";
import { CustomEvent } from "schemas/functions";
import { DisplayName, Plans } from "schemas/payments";

import { VA_MONEY_BACK_GUARANTEE } from "constants/influencer";
import { fetcherAuth } from "utils/api";
import { trackEvent } from "utils/tracking";

import { StripeCustomer } from "../schema";
import { useCheckout } from "../useCheckout";
import BillingAddressForm from "./AddressForm";

interface Props {
  handleConfirm: () => Promise<any>;
  returnUrl: string;
  setUpClientSecret?: string;
  totalDue?: number;
}

const PaymentForm = ({
  handleConfirm,
  returnUrl,
  setUpClientSecret,
  totalDue,
}: Props) => {
  const { setErrorAlert } = useContext(AlertContext);
  const { getToken } = useAuth();
  const { currentOrg } = useContext(OrganizationUserContext);

  const { plans } = useContext(SubscriptionContext);
  const { isVaMoneyBack, updatePreviewInvoice } = useCheckout();
  const {
    plan,
    customer,
    setCustomer,
    noNeedsToPay,
    requiresBillingAddress,
    source,
  } = useContext(CheckoutContext);
  const theme = useTheme();
  const isMobileScreen = useMediaQuery(theme.breakpoints.down("md"));

  const [editAddress, setEditAddress] = useState(false);
  const [addressUpdateLoading, setAddressUpdateLoading] = useState(false);
  const [terms, setTerms] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>("");
  const [openBottomDrawer, setOpenBottomDrawer] = useState(false);

  const stripe = useStripe();
  const elements = useElements();

  const handleSubmit = async (event: CustomEvent) => {
    event.preventDefault();

    if (!stripe || !elements || !setUpClientSecret) {
      // Stripe.js hasn't yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      return;
    }

    if (!customer.address?.line1) {
      setError("Please fill out address.");
      return;
    }

    if (!customer.address?.country) {
      setError("Please fill out country.");
      return;
    }

    try {
      const { error: submitError } = await elements.submit();
      if (submitError) {
        setError(
          submitError.message ||
            "Something went wrong. Please refresh and try again.",
        );
        return;
      }
      setLoading(true);

      const { error, setupIntent } = await stripe.confirmSetup({
        //`Elements` instance that was used to create the Payment Element
        elements,
        clientSecret: setUpClientSecret,
        redirect: "if_required",
        confirmParams: {
          return_url: returnUrl,
          payment_method_data: {
            billing_details: {
              address: {
                country: customer?.address?.country,
                postal_code: customer?.address?.postal_code,
              },
            },
          },
        },
      });
      if (error) {
        setError(
          error.message ||
            "Something went wrong. Please refresh and try again.",
        );
        return;
      }
      if (setupIntent?.status === "succeeded" && setupIntent?.payment_method) {
        await updateCustomer(customer, setupIntent.payment_method as string);
        await handleConfirm();
      }
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: CustomEvent) => {
    if (e.value.address?.line1 && error === "Please fill out address.") {
      setError("");
    } else if (
      e.value.address?.country &&
      error === "Please fill out country."
    ) {
      setError("");
    } else if (
      e.value.address?.postal_code &&
      error === "Please fill out postal code."
    ) {
      setError("");
    }
    if (e.value.address?.postal_code || e?.value?.address?.country) {
      updateCustomer(
        {
          ...customer,
          address: e.value.address,
        },
        undefined,
        true,
      );
    }
    setError("");
  };

  const updateCustomer = async (
    customer: StripeCustomer,
    paymentMethodId?: string,
    autoUpdate?: boolean,
  ) => {
    setAddressUpdateLoading(true);
    try {
      const { customer: updatedCustomer } = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/payments/customer`,
        "PUT",
        {},
        {
          customerId: customer?.id,
          country: customer?.address?.country,
          postalCode: customer?.address?.postal_code,
          line1: customer?.address?.line1,
          line2: customer?.address?.line2,
          state: customer?.address?.state,
          city: customer?.address?.city,
          paymentMethodId,
          source,
        },
      );
      setCustomer(updatedCustomer);
      await updatePreviewInvoice({
        plan,
        organizationId: currentOrg?.id,
        customer: updatedCustomer,
      });
      setError("");
    } catch (error) {
      if (!autoUpdate && error?.message?.includes("Invalid address")) {
        if (!customer.address?.line1) {
          setError("Please fill out address.");
          return;
        }
        if (!customer.address?.country) {
          setError("Please fill out country.");
          return;
        }
        if (!customer.address?.postal_code) {
          setError("Please fill out postal code.");
          return;
        }
        return;
      }
      if (!autoUpdate) {
        setError(error?.message);
      }
    } finally {
      setAddressUpdateLoading(false);
    }
  };

  const onSubmit = (e: CustomEvent) => {
    e.preventDefault();
    if (setUpClientSecret) {
      handleSubmit(e);
    }
  };

  useEffect(() => {
    if (isVaMoneyBack) {
      setTerms(VA_MONEY_BACK_GUARANTEE);
    } else if (plans && plan && plans[plan as Plans]?.terms_and_conditions) {
      setTerms(plans[plan as Plans]?.terms_and_conditions || "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plan]);

  const formatBillingAddress = () => {
    let address = "";
    if (customer?.address?.line1) {
      address += customer.address.line1;
    }
    if (customer?.address?.line2) {
      address += `, ${customer.address.line2}`;
    }
    if (customer?.address?.city) {
      address += `, ${customer.address.city}`;
    }
    if (customer?.address?.state) {
      address += `, ${customer.address.state}`;
    }
    if (customer?.address?.postal_code) {
      address += `, ${customer.address.postal_code}`;
    }
    if (customer?.address?.country) {
      address += `, ${customer.address.country}`;
    }
    return address;
  };

  const renderBody = () => {
    return (
      <form onSubmit={onSubmit}>
        <Box sx={{ px: 3 }}>
          {noNeedsToPay && (
            <Typography sx={{ my: 2 }}>
              We will <strong>not charge</strong> your card at this time. Your
              invoice will be deducted from your current credit balance.
              However, we will use this payment method for future payments.
            </Typography>
          )}

          <PaymentElement
            options={{
              layout: {
                type: "tabs",
                defaultCollapsed: false,
              },
              fields: {
                billingDetails: {
                  address: { country: "never", postalCode: "never" },
                },
              },
              terms: {
                card: "never",
                applePay: "never",
                googlePay: "never",
              },
            }}
            onChange={handleChange}
          />

          <Box sx={{ mt: 3 }}>
            <Typography variant="caption" color="textSecondary">
              Billing Address{" "}
            </Typography>
            {requiresBillingAddress || editAddress ? (
              <BillingAddressForm handleChange={handleChange} />
            ) : (
              <Grid container alignItems={"center"} sx={{ mb: 3 }}>
                <Typography component="span">
                  {formatBillingAddress()}{" "}
                  <IconButton
                    sx={{ width: 20, height: 20 }}
                    onClick={() => setEditAddress(true)}
                  >
                    <Box
                      component="i"
                      className="fa-regular fa-pen-to-square"
                      sx={{ ml: 1, fontSize: 14, color: "purple.main" }}
                    />
                  </IconButton>
                </Typography>
              </Grid>
            )}

            {error && (
              <Typography sx={{ mb: 2 }} color="error">
                {error}
              </Typography>
            )}
          </Box>

          <LoadingButton
            variant="contained"
            fullWidth
            type="submit"
            onClick={onSubmit}
            loading={addressUpdateLoading || loading}
            disabled={error !== ""}
          >
            {totalDue ? <>Pay ${totalDue.toFixed(2)}</> : <>Subscribe</>}
          </LoadingButton>

          <FormHelperText sx={{ my: 3 }}>
            By confirming, you agree to{" "}
            {terms && (
              <>
                <Link href={terms} target="_blank">
                  <strong>{DisplayName[plan as Plans]}'s Terms</strong>
                </Link>{" "}
                and{" "}
              </>
            )}
            <Link
              href="https://www.onbento.com/terms-of-services"
              target="_blank"
            >
              <strong>Bento's Terms of Service</strong>
            </Link>
          </FormHelperText>
        </Box>
      </form>
    );
  };

  return (
    <>
      {isMobileScreen && (
        <>
          <Button
            fullWidth
            variant="contained"
            disableElevation
            onClick={(e) => {
              trackEvent("Stripe Checkout - Open Mobile Drawer", {
                source,
              });
              e.preventDefault();
              setOpenBottomDrawer(true);
            }}
          >
            Pay
          </Button>
          <Drawer
            anchor={"bottom"}
            open={openBottomDrawer}
            onClose={() => {
              trackEvent("Stripe Checkout - Close Mobile Drawer", {
                source,
              });
              setOpenBottomDrawer(false);
            }}
            sx={{ zIndex: 1300 }}
          >
            <Grid
              container
              justifyContent={"space-between"}
              sx={{ p: 2 }}
              alignItems="center"
            >
              <Typography variant="h4" sx={{ pl: 1 }}>
                Check out{" "}
              </Typography>
              <IconButton
                sx={{ fontSize: 16 }}
                onClick={() => {
                  trackEvent("Stripe Checkout - Close Mobile Drawer", {
                    source,
                  });
                  setOpenBottomDrawer(false);
                }}
              >
                <Box component="i" className="fa-regular fa-xmark" />
              </IconButton>
            </Grid>

            {renderBody()}
          </Drawer>
        </>
      )}

      {!isMobileScreen && (
        <>
          {" "}
          <Typography variant="h4" gutterBottom sx={{ pl: 3 }}>
            Check out{" "}
          </Typography>
          {renderBody()}
        </>
      )}
    </>
  );
};

export default PaymentForm;
